import { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { UI_LANGUAGES } from '@/lib/ui-languages';
import type { Language as AppLanguage } from '@/stores/language.store';

interface HeaderLanguageSelectorProps {
  className?: string;
  buttonClassName?: string;
}

export function HeaderLanguageSelector({ className = '', buttonClassName = '' }: HeaderLanguageSelectorProps) {
  const { language, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang: AppLanguage) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 group text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white ${buttonClassName}`}
        aria-label="Sélectionner la langue"
      >
        <Globe className="h-4 w-4 text-[#475569] group-hover:text-[#2563EB] transition-colors dark:text-slate-200 dark:group-hover:text-white" />
      </button>

      {isOpen && (
        <div className="absolute end-0 z-50 mt-2 w-52 rounded-2xl border border-[#E2E8F0] bg-white py-2 shadow-xl backdrop-blur-lg dark:border-slate-600 dark:bg-slate-900">
          {UI_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguageSelect(lang.code as AppLanguage)}
              className={`flex w-full items-center justify-between px-4 py-2 text-start text-sm transition-colors ${
                language === lang.code
                  ? 'bg-[#DBEAFE] text-[#2563EB] dark:bg-white/10 dark:text-white'
                  : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white'
              }`}
            >
              <span lang={lang.code}>{`${lang.flag} ${lang.label}`}</span>
              {language === lang.code && <Check className="h-4 w-4 shrink-0 text-[#2563EB]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
